// src/components/subscription/UsageStatsBar.tsx
import React from 'react';
import { Users, CheckSquare, UserCircle, Brain, Info } from 'lucide-react';

interface UsageItemProps {
  icon: React.ReactNode;
  label: string;
  current: number;
  limit: number;
  description?: string;
}

const UsageItem: React.FC<UsageItemProps> = ({ icon, label, current, limit, description }) => {
  // Calculate the percentage used
  const percentage = Math.min(Math.round((current / limit) * 100), 100);
  
  // Determine color based on usage percentage
  const getBarColor = () => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-blue-500';
  };
  
  return (
    <div className="mb-4">
      <div className="flex items-center mb-1">
        <div className="text-gray-600 mr-2">{icon}</div>
        <span className="text-sm font-medium">{label}</span>
        <div className="ml-auto text-sm text-gray-600">
          {current} / {limit}
        </div>
      </div>
      
      <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full ${getBarColor()}`} 
          style={{ width: `${percentage}%` }}
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
        ></div>
      </div>
      
      {description && (
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      )}
    </div>
  );
};

interface UsageStatsBarProps {
  aiQueries: { count: number; limit: number };
  contacts: { count: number; limit: number };
  todos: { count: number; limit: number };
  groups: { count: number; limit: number };
  plan: 'free' | 'medium' | 'premium';
  showUpgradeInfo?: boolean;
  nextTierLimits?: {
    aiQueries: number;
    contacts: number;
    todos: number;
    groups: number;
  };
  className?: string;
}

const UsageStatsBar: React.FC<UsageStatsBarProps> = ({
  aiQueries,
  contacts,
  todos,
  groups,
  plan,
  showUpgradeInfo = false,
  nextTierLimits,
  className = ''
}) => {
  const getUpgradeMessage = () => {
    if (plan === 'premium') return null;
    
    const nextTier = plan === 'free' ? 'Medium' : 'Premium';
    
    return (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
        <div className="flex items-start">
          <Info size={16} className="text-blue-500 mt-1 mr-2 flex-shrink-0" />
          <div>
            <p className="text-sm text-blue-800">
              <span className="font-medium">Upgrade to {nextTier}</span> to increase your limits:
            </p>
            {nextTierLimits && (
              <ul className="text-xs text-blue-700 mt-1 space-y-1">
                <li>• AI Queries: {nextTierLimits.aiQueries} per month</li>
                <li>• Contacts: {nextTierLimits.contacts === Infinity ? 'Unlimited' : nextTierLimits.contacts} total</li>
                <li>• TODOs: {nextTierLimits.todos === Infinity ? 'Unlimited' : nextTierLimits.todos} total</li>
                <li>• Groups: {nextTierLimits.groups === Infinity ? 'Unlimited' : nextTierLimits.groups} total</li>
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderUsageItem = (icon: React.ReactNode, label: string, current: number, limit: number, description: string) => {
    const isUnlimited = limit === Infinity;
    const percentage = isUnlimited ? 0 : Math.min(100, (current / limit) * 100);
    const progressColor = isUnlimited ? 'bg-green-500' : percentage >= 80 ? 'bg-red-500' : 'bg-blue-500';

    return (
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            {icon}
            <span className="font-medium">{label}</span>
          </div>
          <span className="text-sm text-gray-600">
            {current} {isUnlimited ? '∞' : `of ${limit}`}
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full ${progressColor} transition-all duration-300`}
            style={{ width: isUnlimited ? '100%' : `${percentage}%` }}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">{description}</p>
      </div>
    );
  };

  return (
    <div className={`p-4 bg-white rounded-lg border ${className}`}>
      <h3 className="text-lg font-semibold mb-4">Usage Statistics</h3>
      
      {showUpgradeInfo && getUpgradeMessage()}
      
      {renderUsageItem(
        <Brain size={16} />,
        "AI Queries",
        aiQueries.count,
        aiQueries.limit,
        "Monthly AI assistant interactions"
      )}
      
      {renderUsageItem(
        <UserCircle size={16} />,
        "Contacts",
        contacts.count,
        contacts.limit,
        "Total contacts in your account"
      )}
      
      {renderUsageItem(
        <CheckSquare size={16} />,
        "TODOs",
        todos.count,
        todos.limit,
        "Total todos across all contacts"
      )}
      
      {renderUsageItem(
        <Users size={16} />,
        "Groups",
        groups.count,
        groups.limit,
        "Total contact groups"
      )}
      
      <p className="text-xs text-gray-500 mt-2">
        Last updated: {new Date().toLocaleString()}
      </p>
    </div>
  );
};

export default UsageStatsBar;